/**
 * Japanese formatting helpers — used by the JP-mode UI widgets (元号 year,
 * 千/百万/億 unit picker, half/full-width digit toggle, furigana display).
 *
 * Pure functions; the UI toggles live in a zustand store (see
 * `lib/jpMode.ts`) so any component can opt into the formatting.
 */

const ERA_BOUNDARIES = [
  { name: '令和', start: new Date('2019-05-01'), startYearAd: 2019 },
  { name: '平成', start: new Date('1989-01-08'), startYearAd: 1989 },
  { name: '昭和', start: new Date('1926-12-25'), startYearAd: 1926 },
  { name: '大正', start: new Date('1912-07-30'), startYearAd: 1912 },
  { name: '明治', start: new Date('1868-10-23'), startYearAd: 1868 },
] as const;

/**
 * Convert a Date (or ISO string) into a 元号 (Japanese era) year string
 * like "令和7年". Pre-Meiji dates fall back to AD.
 */
export function toWareki(value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  for (const era of ERA_BOUNDARIES) {
    if (d >= era.start) {
      const eraYear = d.getFullYear() - era.startYearAd + 1;
      return `${era.name}${eraYear === 1 ? '元' : eraYear}年`;
    }
  }
  return `${d.getFullYear()}年`;
}

/**
 * Format a number into a JP unit string: 千 (1000), 百万 (10^6), 億 (10^8).
 * Returns the value scaled + the suffix, picking the largest unit that
 * yields ≥ 1.0 (or returns the bare number for values < 1000).
 */
export function toJpUnit(n: number, opts: { decimals?: number } = {}): string {
  const d = opts.decimals ?? 2;
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e8) return `${sign}${(abs / 1e8).toFixed(d).replace(/\.?0+$/, '')}億`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(d).replace(/\.?0+$/, '')}百万`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(d).replace(/\.?0+$/, '')}千`;
  return `${sign}${abs.toLocaleString('ja-JP')}`;
}

/**
 * Convert half-width digits (0-9) to full-width (０-９). Half-width is
 * standard in tabular data; full-width is standard in formal JP prose.
 */
export function toFullWidthDigits(s: string): string {
  return s.replace(/[0-9]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0xfee0));
}

/**
 * Convert full-width digits (０-９) to half-width (0-9).
 */
export function toHalfWidthDigits(s: string): string {
  return s.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
}

/**
 * Format a number using a JP-aware combination of the helpers above.
 * Useful as the canonical "currency-like display" formatter in JP mode.
 */
export function formatJpNumber(
  n: number,
  opts: { mode?: 'unit' | 'wide' | 'plain'; decimals?: number } = {},
): string {
  if (opts.mode === 'unit') return toJpUnit(n, { decimals: opts.decimals });
  const base = n.toLocaleString('ja-JP', {
    maximumFractionDigits: opts.decimals ?? 0,
  });
  if (opts.mode === 'wide') return toFullWidthDigits(base);
  return base;
}
